import { Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTheme } from './ThemeProvider';

/**
 * The light/dark switch.
 *
 * A labelled two-option control rather than a bare icon. The bare icon was easy to miss next to the
 * notification bell, and it showed only the theme you would switch *to*, so nobody could tell at a
 * glance which theme was on. Both options are visible here and the active one is filled.
 *
 * Used in the app's top bar and on the sign-in page, so a theme can be chosen before signing in.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg border border-border-default bg-surface-sunken p-0.5',
        className,
      )}
    >
      {(
        [
          ['light', 'Light', Sun],
          ['dark', 'Dark', Moon],
        ] as const
      ).map(([value, label, Icon]) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => {
              if (!active) toggleTheme();
            }}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-caption font-medium transition-colors',
              active
                ? 'bg-surface text-text-primary shadow-xs'
                : 'text-text-muted hover:text-text-primary',
            )}
          >
            <Icon aria-hidden className="size-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
